// src/components/InvitePlayersModal.tsx
import { logger } from '../utils/logger';
// 🛡️ ENHANCED: Block protection - can't invite blocked users or users who blocked you

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonSpinner,
  IonText,
  IonAvatar,
  IonSearchbar,
  IonToast,
} from '@ionic/react';
import { close, checkmark } from 'ionicons/icons';
import { getConnections, Connection } from '../services/connectionsService';
import { sessionService } from '../services/sessionService';
import { filterBlockedUsers } from '../services/blockProtectionService';
import { AvatarPreview } from './avatar/AvatarPreview';

interface InvitePlayersModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  sessionId: string;
  currentParticipantIds: string[];
  onInviteComplete?: () => void;
}

interface ConnectionDisplay {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  avatar?: any;
  is_admin?: boolean;
}

export const InvitePlayersModal: React.FC<InvitePlayersModalProps> = ({
  isOpen,
  onDismiss,
  sessionId,
  currentParticipantIds,
  onInviteComplete,
}) => {
  const [connections, setConnections] = useState<ConnectionDisplay[]>([]);
  const [filteredConnections, setFilteredConnections] = useState<ConnectionDisplay[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadConnections();
      setSelectedUserIds(new Set());
      setSearchText('');
    }
  }, [isOpen, sessionId]);

  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredConnections(connections);
    } else {
      const search = searchText.toLowerCase();
      setFilteredConnections(
        connections.filter(
          c =>
            c.username.toLowerCase().includes(search) ||
            c.display_name.toLowerCase().includes(search)
        )
      );
    }
  }, [searchText, connections]);

  const loadConnections = async () => {
    setLoading(true);

    const result = await getConnections();

    if (result.success && result.connections) {
      // Get accepted connections who aren't already participants
      const accepted = result.connections
        .filter((c: Connection) => c.status === 'accepted')
        .filter((c: Connection) => !currentParticipantIds.includes(c.other_user.id))
        .map((c: Connection) => ({
          id: c.other_user.id,
          username: c.other_user.username,
          display_name: c.other_user.display_name,
          avatar_url: c.other_user.avatar_url,
          avatar: c.other_user.avatar,
          is_admin: c.other_user.is_admin,
        }));

      // Filter out blocked users
      const userIds = accepted.map(c => c.id);
      const allowedIds = await filterBlockedUsers(userIds);
      const allowedSet = new Set(allowedIds);

      const availableConnections = accepted.filter(c => allowedSet.has(c.id));

      setConnections(availableConnections);
      setFilteredConnections(availableConnections);
    } else {
      logger.error('Failed to load connections:', result.error);
      setConnections([]);
      setFilteredConnections([]);
    }

    setLoading(false);
  };

  const toggleUser = (userId: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  const handleInvite = async () => {
    if (selectedUserIds.size === 0) return;

    setInviting(true);

    const userIdsArray = Array.from(selectedUserIds);
    const result = await sessionService.inviteParticipants(sessionId, userIdsArray);

    if (result.success) {
      setToastMessage(`Invited ${selectedUserIds.size} ${selectedUserIds.size === 1 ? 'player' : 'players'}`);
      setShowToast(true);

      if (onInviteComplete) {
        onInviteComplete();
      }

      setTimeout(() => {
        onDismiss();
      }, 1000);
    } else {
      setToastMessage(result.error || 'Failed to invite players');
      setShowToast(true);
    }

    setInviting(false);
  };

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onDismiss} disabled={inviting}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
            <IonTitle>Invite Players</IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={handleInvite}
                disabled={selectedUserIds.size === 0 || inviting}
                strong
              >
                {inviting ? <IonSpinner name="crescent" /> : <IonIcon icon={checkmark} />}
              </IonButton>
            </IonButtons>
          </IonToolbar>
          <IonToolbar>
            <IonSearchbar
              value={searchText}
              onIonInput={(e) => setSearchText(e.detail.value!)}
              placeholder="Search connections..."
              disabled={inviting}
            />
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <IonSpinner name="crescent" />
            </div>
          ) : filteredConnections.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '20px', textAlign: 'center' }}>
              <IonText color="medium">
                {connections.length === 0 ? (
                  <>
                    <p>No available connections to invite.</p>
                    <p style={{ fontSize: '14px', marginTop: '8px' }}>
                      All your connections are already participants or have been blocked.
                    </p>
                  </>
                ) : (
                  <p>No connections match your search.</p>
                )}
              </IonText>
            </div>
          ) : (
            <>
              <div style={{ padding: '16px 16px 8px 16px' }}>
                <IonText color="medium">
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    {selectedUserIds.size > 0
                      ? `${selectedUserIds.size} ${selectedUserIds.size === 1 ? 'player' : 'players'} selected`
                      : 'Select players to invite'}
                  </p>
                </IonText>
              </div>

              <IonList>
                {filteredConnections.map((connection) => (
                  <IonItem
                    key={connection.id}
                    button
                    onClick={() => !inviting && toggleUser(connection.id)}
                    disabled={inviting}
                  >
                    <IonAvatar slot="start">
                      {connection.avatar_url ? (
                        <img
                          src={connection.avatar_url}
                          alt={connection.display_name || connection.username}
                        />
                      ) : connection.avatar ? (
                        <AvatarPreview
                          avatarData={connection.avatar}
                          size={40}
                          userId={connection.id}
                          isAdmin={connection.is_admin || false}
                        />
                      ) : (
                        <AvatarPreview size={40} userId={connection.id} />
                      )}
                    </IonAvatar>
                    <IonLabel>
                      <h2>{connection.display_name || connection.username}</h2>
                      {connection.display_name && <p>@{connection.username}</p>}
                    </IonLabel>
                    <IonCheckbox
                      slot="end"
                      checked={selectedUserIds.has(connection.id)}
                      disabled={inviting}
                    />
                  </IonItem>
                ))}
              </IonList>
            </>
          )}
        </IonContent>
      </IonModal>

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={2000}
        color={toastMessage.includes('Failed') ? 'danger' : 'success'}
        aria-live="polite"
      />
    </>
  );
};